import { S3Client, GetObjectCommand, ListObjectsV2Command, NoSuchKey, S3ServiceException } from '@aws-sdk/client-s3';
import { InvokeCommand, LambdaClient } from '@aws-sdk/client-lambda';
import { type Credentials } from '@aws-sdk/client-sts';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { v4 as uuid } from 'uuid';

import { getIdToken } from '@/auth';
import Log from '@/Logger';

import type { NormalizedCredentials, ListObjectItem } from './types';
import { S3ClientError } from './errors';

// 一時的な認証情報をキャッシュする変数
let __cachedCreds: NormalizedCredentials | null = null;
function getCredentialsCache(): NormalizedCredentials | null { return __cachedCreds; }
function setCredentialsCache(creds: NormalizedCredentials | null) { __cachedCreds = creds; }
// 認証情報取得中のPromiseを保持する変数（多重リクエスト防止用）
let credsPromise: Promise<NormalizedCredentials> | null = null;
// S3クライアントのインスタンスをキャッシュ
let s3Client: S3Client | null = null;

/**
 * レスポンスオブジェクトから `$metadata` プロパティを取得します。
 *
 * @param resp - メタデータを含む可能性のあるレスポンスオブジェクト
 * @returns `$metadata` プロパティ、存在しない場合は `undefined`
 */
function getMetadata(resp: any) {
    return resp?.['$metadata'];
}

/**
 * Lambda経由で一時的なS3アクセス用認証情報を取得します。
 * 失敗時はApiErrorを投げます。
 */
async function fetchCredentials(): Promise<NormalizedCredentials> {
    const logger = Log.getLogger();
    let credentials: Credentials | undefined = undefined;
    try {
        // セッションからJWTトークンを取得
        const jwt = (await getIdToken()).toString();
        // Cognito Identity Pool認証情報を渡してLambdaクライアントを作成
        const client = new LambdaClient({
            region: import.meta.env.VITE_AWS_REGION,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: import.meta.env.VITE_AWS_REGION },
                identityPoolId: import.meta.env.VITE_AWS_IDENTITY_POOL_ID as string,
                logins: {
                    ['cognito-idp.ap-northeast-1.amazonaws.com/' + import.meta.env.VITE_AWS_USER_POOL_ID as string]: jwt,
                },
            })
        });
        // Lambda関数(dalmatian-lantana-web-assumerole-dev)をInvokeして一時的なS3認証情報を取得
        logger.api.info('Lambda', 'Start to request: InvokeCommand');
        const command = new InvokeCommand({
            FunctionName: import.meta.env.VITE_AWS_S3_TOKEN,
            Payload: new TextEncoder().encode(`{ "requestId": "${uuid()}", "token": "${jwt}" }`)
        });
        const response = await client.send(command);
        logger.api.info('Lambda', 'Succeed to request: InvokeCommand', getMetadata(response));

        // レスポンスから認証情報を抽出
        const body = JSON.parse(new TextDecoder().decode(response.Payload)) as { Credentials?: Credentials; };
        credentials = body.Credentials;

    } catch (err) {
        logger.api.error('Cognito', 'Fail to request: currentSession', err);
        throw new S3ClientError('Cognito: Fail to request: currentSession', err);
    }

    // 必須フィールドがなければエラー
    if (!credentials?.AccessKeyId || !credentials?.SecretAccessKey) {
        throw new S3ClientError('Cognito: Credentials not found');
    }

    // 正規化した認証情報を返却
    return {
        accessKeyId: credentials.AccessKeyId,
        secretAccessKey: credentials.SecretAccessKey,
        sessionToken: credentials.SessionToken,
        expiration: credentials.Expiration ? new Date(credentials.Expiration).getTime() : undefined,
    } as NormalizedCredentials;
}

/**
 * 有効な認証情報を返します（キャッシュ・期限切れ判定付き）。
 */
async function ensureCredentials(): Promise<NormalizedCredentials> {
    // 取得中なら同じPromiseを返す
    if (credsPromise) {
        return credsPromise;
    }

    // 新規取得
    credsPromise = (async () => {
        try {
            const c = await fetchCredentials();
            setCredentialsCache(c);
            return c;
        } finally {
            credsPromise = null;
        }
    })();

    return credsPromise;
}

/**
 * 指定した認証情報でS3クライアントを生成します。
 */
function buildS3Client(creds: NormalizedCredentials) {
    return new S3Client({
        region: import.meta.env.VITE_AWS_REGION,
        credentials: {
            accessKeyId: creds.accessKeyId,
            secretAccessKey: creds.secretAccessKey,
            sessionToken: creds.sessionToken,
        },
    });
}

function invalidateCache() {
    s3Client = null;
    credsPromise = null;
    setCredentialsCache(null);
}

function getCacheExpiration(): number | undefined {
    return getCredentialsCache()?.expiration;
}

function isValidClient(): boolean {
    const now = Date.now();
    const exp = getCacheExpiration();
    return Boolean(s3Client && (exp === undefined || (exp - 5000 > now)));
}

/**
 * 有効なS3クライアントを返します（認証情報の期限切れ時は再生成）。
 */
async function getClient(): Promise<S3Client> {
    if (!isValidClient()) {
        // 認証情報の期限切れや変更時はクライアントを再生成
        const creds = await ensureCredentials();
        s3Client = buildS3Client(creds);
    }
    return s3Client!;
}

/**
 * S3からオブジェクトを取得します。
 */
export async function getObject(key: string) {
    try {
        const client = await getClient();
        const cmd = new GetObjectCommand({
            Bucket: import.meta.env.VITE_AWS_BUCKET_NAME,// バケットは固定
            Key: key
        });
        const res = await client.send(cmd);
        return res;
    } catch (err) {
        if (err instanceof NoSuchKey) {
            throw new S3ClientError('getObject failed', err, 404); // Not Found
        } else if (err instanceof S3ServiceException) {
            throw new S3ClientError('getObject failed', err, err.$response?.statusCode);
        } else {
            throw new S3ClientError('getObject failed', err);
        }
    }
}

/**
 * S3オブジェクトをUint8Arrayとして取得します。
 */
export async function getObjectAsUint8Array(key: string): Promise<Uint8Array> {
    const res = await getObject(key);
    return await res.Body?.transformToByteArray() ?? new Uint8Array();
}

/**
 * S3オブジェクトをBlobとして取得します。
 */
export async function getObjectAsBlob(key: string, options?: BlobPropertyBag): Promise<Blob> {
    const res = await getObject(key);
    // ArrayBufferに変換してBlobを生成
    const u8 = await res.Body?.transformToByteArray();
    if (!u8 || u8.length === 0)
        throw new S3ClientError('getObjectAsBlob returns empty data', undefined, 404); // Not Found
    // Blobに変換
    const ab = u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
    return new Blob([ab], { ...{ type: res.ContentType }, ...options });
}

/**
 * S3バケット内のオブジェクト一覧を取得します。
 */
export async function listObjects(prefix?: string): Promise<Array<ListObjectItem>> {
    try {
        const client = await getClient();
        const cmd = new ListObjectsV2Command({
            Bucket: import.meta.env.VITE_AWS_BUCKET_NAME,// バケットは固定
            Prefix: prefix,
        });
        const res = await client.send(cmd);
        const contents = res.Contents ?? [];
        // 必要な情報だけ抽出して返却
        return contents.map(c => ({ Key: c.Key as string, LastModified: c.LastModified?.toString(), Size: c.Size }));
    } catch (err) {
        throw new S3ClientError('listObjects failed', err);
    }
}

/**
 * S3オブジェクトの署名付きURLを取得します。
 */
export async function getPresignedUrl(key: string, expiresInSec = 900): Promise<string> {
    try {
        const client = await getClient();
        const cmd = new GetObjectCommand({
            Bucket: import.meta.env.VITE_AWS_BUCKET_NAME,// バケットは固定
            Key: key,
        });
        const url = await getSignedUrl(client, cmd, { expiresIn: expiresInSec });
        return url;
    } catch (err) {
        throw new S3ClientError('getPresignedUrl failed', err);
    }
}

if (import.meta.env.NODE_ENV === 'test') {
    exports.getCredentialsCache = getCredentialsCache;
    exports.setCredentialsCache = setCredentialsCache;
    exports.invalidateCache = invalidateCache;
    exports.isValidClient = isValidClient;
    exports.getCacheExpiration = getCacheExpiration;
}